// TradingJournal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Goal, Skull, Calendar, TrendingUp, TrendingDown, Target, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import './TradingJournal.css';

// ── 차트 상태별 dot 색상 ──────────────────────────────
const STATUS_DOT_COLOR = {
  BUY:         '#22c55e',
  SELL:        '#ef4444',
  HOLDING:     '#3b82f6',
  NO_POSITION: '#d1d5db',
};

const ScoreDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;
  const color = STATUS_DOT_COLOR[payload?.status] || '#d1d5db';
  return <circle cx={cx} cy={cy} r={3.5} fill={color} stroke="#fff" strokeWidth={1.5} />;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const priceEntry = payload.find(p => p.dataKey === 'price');
  const scoreEntry = payload.find(p => p.dataKey === 'score');
  return (
    <div className="journal-chart-tooltip">
      <p className="journal-chart-tooltip-date">{label}</p>
      {priceEntry && (
        <p className="journal-chart-tooltip-price">
          ${priceEntry.value?.toLocaleString()}
        </p>
      )}
      {scoreEntry && (
        <p className="journal-chart-tooltip-score">
          AI 점수 {scoreEntry.value > 0 ? '+' : ''}{scoreEntry.value}
        </p>
      )}
    </div>
  );
};

const filterByRange = (data, range) => {
  if (range === 'ALL' || !data.length) return data;
  const months = { '1M': 1, '3M': 3, '6M': 6 };
  const last = new Date(data[data.length - 1].date);
  const cutoff = new Date(last);
  cutoff.setMonth(cutoff.getMonth() - months[range]);
  return data.filter(d => new Date(d.date) >= cutoff);
};

const PAGE_SIZE = 20;

// ── JournalChart ───────────────────────────────────────
const JournalChart = ({ scoreChartData }) => {
  const [range, setRange] = useState('ALL');
  const [showPrice, setShowPrice] = useState(true);
  const [showScore, setShowScore] = useState(true);
  const [visibleStart, setVisibleStart] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const touchStartX = useRef(null);

  const allChartData = (scoreChartData || [])
    .filter(item => item.scoreDate && item.stockPrice)
    .map(item => ({
      date: item.scoreDate,
      price: parseFloat(item.stockPrice),
      score: item.score ?? 0,
      status: item.status,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const filteredData = filterByRange(allChartData, range);

  // 범위/데이터 변경 시 최신 페이지로 초기화
  useEffect(() => {
    setVisibleStart(Math.max(0, filteredData.length - PAGE_SIZE));
  }, [range, filteredData.length]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const currentPage = Math.floor(visibleStart / PAGE_SIZE); // 0-based
  const visibleData = showAll ? filteredData : filteredData.slice(visibleStart, visibleStart + PAGE_SIZE);

  const goLeft = useCallback(() => {
    setVisibleStart(prev => Math.max(0, prev - PAGE_SIZE));
  }, []);

  const goRight = useCallback(() => {
    setVisibleStart(prev => {
      const max = Math.max(0, filteredData.length - PAGE_SIZE);
      return Math.min(max, prev + PAGE_SIZE);
    });
  }, [filteredData.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goRight(); // 왼쪽 스와이프 → 최신
      else goLeft();         // 오른쪽 스와이프 → 과거
    }
    touchStartX.current = null;
  };

  const canGoLeft = visibleStart > 0;
  const canGoRight = visibleStart + PAGE_SIZE < filteredData.length;

  if (!allChartData.length) return null;

  return (
    <div className="journal-chart-wrap">
      <div className="journal-chart-header">
        <div className="journal-chart-legend">
          <button
            className={`legend-price legend-toggle${showPrice ? '' : ' legend-toggle--off'}`}
            onClick={() => setShowPrice(v => !v)}
          >
            <span className="legend-price-line" />
            종가
          </button>
          <button
            className={`legend-score legend-toggle${showScore ? '' : ' legend-toggle--off'}`}
            onClick={() => setShowScore(v => !v)}
          >
            <span className="legend-score-line" />
            AI 점수
          </button>
        </div>
        <div className="journal-chart-range-tabs">
          {['1M', '3M', '6M', 'ALL'].map(r => (
            <button
              key={r}
              className={`range-tab${range === r ? ' active' : ''}`}
              onClick={() => setRange(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* 차트 영역 (스와이프 가능) */}
      <div
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ userSelect: 'none' }}
      >
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={visibleData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.15} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={d => d.slice(5)}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            {showPrice && (
              <YAxis
                yAxisId="price"
                orientation="left"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                width={38}
              />
            )}
            {!showPrice && <YAxis yAxisId="price" orientation="left" hide width={0} />}
            {showScore && (
              <YAxis
                yAxisId="score"
                orientation="right"
                domain={[-100, 100]}
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={28}
              />
            )}
            {!showScore && <YAxis yAxisId="score" orientation="right" hide width={0} />}
            {showScore && <ReferenceLine yAxisId="score" y={0} stroke="#e5e7eb" strokeDasharray="4 4" />}
            <Tooltip content={<ChartTooltip />} />
            {showPrice && (
              <Area
                yAxisId="price"
                type="monotone"
                dataKey="price"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#priceGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#3b82f6' }}
              />
            )}
            {showScore && (
              <Line
                yAxisId="score"
                type="monotone"
                dataKey="score"
                stroke="#B8A7E8"
                strokeWidth={1.5}
                strokeDasharray="5 3"
                dot={<ScoreDot />}
                activeDot={{ r: 4, fill: '#B8A7E8' }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* 페이징 컨트롤 */}
      {totalPages > 1 && (
        <div className="journal-chart-paging">
          {!showAll && (
            <>
              <button
                className={`chart-page-btn${canGoLeft ? '' : ' chart-page-btn--disabled'}`}
                onClick={goLeft}
                disabled={!canGoLeft}
              >
                <ChevronLeft size={13} />
              </button>

              <div className="chart-page-track">
                <div
                  className="chart-page-thumb"
                  style={{ width: `${100 / totalPages}%`, left: `${(currentPage / totalPages) * 100}%` }}
                />
              </div>

              <span className="chart-page-label">
                {currentPage + 1} / {totalPages}
              </span>

              <button
                className={`chart-page-btn${canGoRight ? '' : ' chart-page-btn--disabled'}`}
                onClick={goRight}
                disabled={!canGoRight}
              >
                <ChevronRight size={13} />
              </button>
            </>
          )}

          <button
            className={`chart-showall-btn${showAll ? ' chart-showall-btn--active' : ''}`}
            onClick={() => setShowAll(v => !v)}
          >
            {showAll ? '페이징' : '전체'}
          </button>
        </div>
      )}
    </div>
  );
};

export const TradingJournal = ({ stockName, scoreHistory = [], scoreChartData = [], simulationResults = [], monitoringDayCount = 0, totalProfit = 0}) => {
  const [showTooltip, setShowTooltip] = useState(null);
  // Transform scoreHistory data to journal format
  const transformScoreHistoryToJournal = (scoreHistory) => {
    if (!scoreHistory || !Array.isArray(scoreHistory)) {
      console.warn('scoreHistory is not a valid array:', scoreHistory);
      return [];
    }

    const transformed = scoreHistory.map((item, index) => {
      let actual = 'neutral';
      let actualPercent = 0;
      
      // Check if required fields exist
      if (!item.scoreDate) {
        console.warn('Missing scoreDate in item:', item);
        return null;
      }
      
      if (item.afterCloseStockPrice && item.beforeCloseStockPrice) {
        const priceChange = item.afterCloseStockPrice - item.beforeCloseStockPrice;
        actualPercent = ((priceChange / item.beforeCloseStockPrice) * 100);
        actual = priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'neutral';
      } else {
        console.warn('Missing stock price data in item:', item);
      }

      // Determine if prediction was correct based on score and actual movement
      const isCorrect = (item.score >= 0 && actual === 'up') || (item.score < 0 && actual === 'down');

      const result = {
        date: item.scoreDate.split('T')[0], // Extract date from ISO string
        actual: actual,
        actualPercent: Math.round(actualPercent * 100) / 100, // Round to 2 decimal places
        prediction: item.score || 0,
        isCorrect: isCorrect,
        status: item.status // Add status from API
      };
      
      return result;
    })
    .filter(item => item !== null)
    //.filter(item => Math.abs(item.prediction) >= 70); // 예측 점수가 70 이상인 것만
    
    const filtered = transformed.filter(item => item.actual !== 'neutral');

    // Sort by date in descending order (newest first)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Use state to handle data updates
  const [journalData, setJournalData] = useState([
  ]);

  // Update journal data when scoreHistory changes
  useEffect(() => {
    if (scoreHistory && scoreHistory.length > 0) {
      const transformedData = transformScoreHistoryToJournal(scoreHistory);
      if (transformedData.length > 0) {
        setJournalData(transformedData);
      } else {
        console.warn('Transformed data is empty, keeping fallback data');
      }
    }
  }, [scoreHistory]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(journalData.length / itemsPerPage);

  // stockName이 변경될 때 currentPage를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [stockName]);

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return journalData.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getActualStatusIcon = (actual) => {
    return actual === 'up' ? (
      <TrendingUp size={13} className="text-green-500" />
    ) : (
      <TrendingDown size={13} className="text-red-500" />
    );
  };

  const getActualStatusText = (actual) => {
    return actual === 'up' ? '상승' : '하락';
  };

  const getActualStatusColor = (actual) => {
    return actual === 'up' ? 'text-green-500' : 'text-red-500';
  };

  const getPredictionColor = (prediction) => {
    if (prediction > 0) return 'text-green-500';
    if (prediction < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const getAccuracyColor = (isCorrect) => {
    return isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600';
  };

  const getStatusDisplay = (status) => {
    switch(status) {
      case 'SELL': return { text: '매도', color: 'text-red-600', badge: 'bg-red-50 text-red-600 border border-red-100' };
      case 'BUY': return { text: '매수', color: 'text-green-600', badge: 'bg-green-50 text-green-600 border border-green-100' };
      case 'HOLDING': return { text: '보유', color: 'text-blue-600', badge: 'bg-blue-50 text-blue-600 border border-blue-100' };
      case 'NO_POSITION': return { text: '미보유', color: 'text-gray-500', badge: 'bg-gray-50 text-gray-500 border border-gray-100' };
      default: return { text: '미보유', color: 'text-gray-500', badge: 'bg-gray-50 text-gray-500 border border-gray-100' };
    }
  };

  const getRowBackgroundColor = (status) => {
    switch(status) {
      case 'SELL': return 'bg-red-50/50 hover:bg-red-50/80';
      case 'BUY': return 'bg-green-50/50 hover:bg-green-50/80';
      case 'HOLDING': return 'bg-blue-50/40 hover:bg-blue-50/70';
      case 'NO_POSITION': return 'hover:bg-gray-50/60';
      default: return 'hover:bg-gray-50/60';
    }
  };

  // 페이지네이션 번호 생성 함수 (축약형)
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    
    if (totalPages <= 7) {
      // 페이지가 7개 이하면 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 페이지가 많을 때 축약형 적용
      if (currentPage <= 4) {
        // 현재 페이지가 앞쪽에 있을 때: 1 2 3 4 5 ... 11
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        // 현재 페이지가 뒤쪽에 있을 때: 1 ... 7 8 9 10 11
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 현재 페이지가 중간에 있을 때: 1 ... 4 5 6 ... 11
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div>

      {/* 통계 요약 */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="journal-stat-card bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Calendar size={13} className="text-gray-400" />
            <p className="text-xs font-medium text-gray-400">거래 추적일</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 leading-none tracking-tight">
            {monitoringDayCount}
            <span className="text-sm font-semibold text-gray-400 ml-1">일</span>
          </p>
        </div>

        <div className={`journal-stat-card rounded-xl p-4 border shadow-sm ${totalProfit >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'}`}>
          <div className="flex items-center gap-1.5 mb-2.5">
            <p className={`text-xs font-medium ${totalProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`}>수익률</p>
            <button
              className="relative"
              onMouseEnter={() => setShowTooltip('simulation')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <Info size={10} className={`hover:opacity-80 ${totalProfit >= 0 ? 'text-blue-300' : 'text-red-300'}`} />
              {showTooltip === 'simulation' && (
                <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-xl z-10">
                  <div className="mb-1.5 font-semibold">시뮬레이션 수익률이란?</div>
                  <div className="text-gray-300 leading-relaxed">
                    AI 점수 70점 이상 매수, -70 이하 매도 기준 누적 수익률입니다.
                  </div>
                </div>
              )}
            </button>
          </div>
          <p className={`text-2xl font-bold leading-none tracking-tight ${totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
            {totalProfit
              ? `${totalProfit > 0 ? '+' : ''}${(totalProfit * 100).toFixed(2)}%`
              : '-%'
            }
          </p>
        </div>
      </div>


      {/* 차트 */}
      <JournalChart scoreChartData={scoreChartData} />

      {/* 테이블 */}
      <div className="journal-table bg-white">
        {/* 테이블 헤더 */}
        <div className="bg-gray-50/80 px-5 py-2.5 border-b border-gray-100">
          <div className="grid grid-cols-11 gap-4 text-xs font-semibold text-gray-400 uppercase tracking-widest">
            <div className="col-span-4">날짜</div>
            <div className="col-span-3">실제주가</div>
            <div className="col-span-2">점수</div>
            <div className="col-span-2">상태</div>
          </div>
        </div>

        {/* 테이블 바디 */}
        <div>
          {getCurrentPageData().map((item, index) => (
            <div
              key={item.date}
              className={`journal-row px-5 py-3.5 border-b border-gray-50 last:border-0 transition-colors duration-150 ${getRowBackgroundColor(item.status)}`}
              style={{ animationDelay: `${index * 45}ms` }}
            >
              <div className="grid grid-cols-11 gap-4 items-center">
                {/* 날짜 */}
                <div className="col-span-4">
                  <p className="text-xs font-semibold text-gray-800 leading-none">{formatDate(item.date)}</p>
                  <p className="text-xs text-gray-400 mt-1">{item.date}</p>
                </div>

                {/* 실제 주가 */}
                <div className="col-span-3">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${item.actual === 'up' ? 'bg-green-50' : 'bg-red-50'}`}>
                      {getActualStatusIcon(item.actual)}
                    </div>
                    <div>
                      <p className={`text-xs font-semibold leading-none ${getActualStatusColor(item.actual)}`}>
                        {item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI 예측 점수 */}
                <div className="col-span-2">
                  <span className={`text-xs font-bold ${getPredictionColor(item.prediction)}`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </span>
                </div>

                {/* 상태 배지 */}
                <div className="col-span-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusDisplay(item.status).badge}`}>
                    {getStatusDisplay(item.status).text}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-5">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="journal-pagination-btn"
          >
            <ChevronLeft size={14} />
          </button>

          <div className="flex gap-0.5">
            {generatePageNumbers(currentPage, totalPages).map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-gray-300"
                  >
                    ···
                  </span>
                );
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`journal-page-num ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="journal-pagination-btn"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// TradingJournalMobile.js - 모바일용 카드 스타일
const TradingJournalMobile = () => {
  const [journalData] = useState([
    
  ]);

  return (
    <div className="feature-section">
      <div className="feature-icon">
        <Calendar size={24} />
      </div>
      <h2 className="text-xl font-bold mb-2 text-black">매매일지</h2>
      <p className="text-gray-600 mb-6">
        실제 주가 움직임과 AI 예측을 비교하여 투자 성과를 분석하고 개선점을 찾아보세요.
      </p>

      <div className="space-y-4">
        {journalData.map((item, index) => (
          <div key={item.date} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-900">{item.date}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                {item.isCorrect ? '정확' : '불일치'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">실제 주가</p>
                <div className="flex items-center space-x-2">
                  {item.actual === 'up' ? (
                    <TrendingUp size={16} className="text-green-500" />
                  ) : (
                    <TrendingDown size={16} className="text-red-500" />
                  )}
                  <span className={`text-sm font-semibold ${item.actual === 'up' ? 'text-green-500' : 'text-red-500'
                    }`}>
                    {item.actual === 'up' ? '상승' : '하락'} ({item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%)
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">AI 예측</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${item.prediction > 0 ? 'bg-green-500' : item.prediction < 0 ? 'bg-red-500' : 'bg-gray-500'
                    }`}>
                    {Math.abs(item.prediction)}
                  </div>
                  <span className={`text-sm font-semibold ${item.prediction > 0 ? 'text-green-500' : item.prediction < 0 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradingJournal;

